import React, { useState, useCallback } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Badge,
} from "@mui/material";
import { Calendar as BigCalendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EventIcon from "@mui/icons-material/Event";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import ChecklistIcon from "@mui/icons-material/Checklist";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import SyncIcon from "@mui/icons-material/Sync";
import PrintIcon from "@mui/icons-material/Print";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ScienceIcon from "@mui/icons-material/Science";
import WarningIcon from "@mui/icons-material/Warning";

const localizer = momentLocalizer(moment);

const TrialCalendarPage = () => {
  const [events, setEvents] = useState([
    {
      id: 1,
      title: "Baseline Visit - ATTR Trial",
      start: new Date(2024, 11, 30, 9, 0),
      end: new Date(2024, 11, 30, 11, 0),
      trialName: "ATTR-ACT Extension",
      visitType: "Baseline",
      location: "Mayo Clinic - Rochester",
      physician: "Dr. Smith",
      notes: "Fasting required - no food after 8pm",
      checklist: [
        { id: 1, task: "Fast 12 hours before appointment", completed: false },
        { id: 2, task: "Bring insurance card", completed: true },
        { id: 3, task: "Bring medication list", completed: true },
        { id: 4, task: "Complete pre-visit questionnaire", completed: false },
      ],
      reminders: true,
      completed: false,
      color: "#1A5EA8",
    },
    {
      id: 2,
      title: "Follow-up Labs - ATTR Trial",
      start: new Date(2025, 0, 15, 8, 30),
      end: new Date(2025, 0, 15, 9, 30),
      trialName: "ATTR-ACT Extension",
      visitType: "Lab Work",
      location: "Mayo Clinic - Lab Services",
      physician: "Lab Tech",
      notes: "NT-proBNP, Troponin, CBC",
      checklist: [
        { id: 1, task: "Fasting required", completed: false },
        { id: 2, task: "Arrive 15 min early", completed: false },
      ],
      reminders: true,
      completed: false,
      color: "#9c27b0",
    },
    {
      id: 3,
      title: "Week 4 Assessment",
      start: new Date(2025, 0, 28, 10, 0),
      end: new Date(2025, 0, 28, 12, 0),
      trialName: "ATTR-ACT Extension",
      visitType: "Follow-up",
      location: "Mayo Clinic - Rochester",
      physician: "Dr. Smith",
      notes: "Review lab results, vital signs, ECG",
      checklist: [
        { id: 1, task: "Bring symptom diary", completed: false },
        { id: 2, task: "List any side effects", completed: false },
        { id: 3, task: "Bring medications", completed: false },
      ],
      reminders: true,
      completed: false,
      color: "#1A5EA8",
    },
  ]);

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [view, setView] = useState("month");
  const [date, setDate] = useState(new Date());

  const [formData, setFormData] = useState({
    title: "",
    trialName: "",
    visitType: "",
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
    location: "",
    physician: "",
    notes: "",
    reminders: true,
    color: "#1A5EA8",
  });

  const visitTypes = [
    "Screening",
    "Baseline",
    "Follow-up",
    "Lab Work",
    "Imaging",
    "ECG/EKG",
    "Echocardiogram",
    "Biopsy",
    "Infusion",
    "Phone Call",
    "End of Study",
    "Other",
  ];

  const trialColors = [
    { label: "Purple", value: "#1A5EA8" },
    { label: "Blue", value: "#2196f3" },
    { label: "Green", value: "#4caf50" },
    { label: "Orange", value: "#ff9800" },
    { label: "Red", value: "#f44336" },
    { label: "Pink", value: "#e91e63" },
  ];

  const handleDialogOpen = (event = null) => {
    if (event) {
      setSelectedEvent(event);
      setFormData({
        title: event.title,
        trialName: event.trialName,
        visitType: event.visitType,
        startDate: moment(event.start).format("YYYY-MM-DD"),
        startTime: moment(event.start).format("HH:mm"),
        endDate: moment(event.end).format("YYYY-MM-DD"),
        endTime: moment(event.end).format("HH:mm"),
        location: event.location || "",
        physician: event.physician || "",
        notes: event.notes || "",
        reminders: event.reminders !== undefined ? event.reminders : true,
        color: event.color || "#1A5EA8",
      });
    } else {
      setSelectedEvent(null);
      setFormData({
        title: "",
        trialName: "",
        visitType: "",
        startDate: moment(date).format("YYYY-MM-DD"),
        startTime: "09:00",
        endDate: moment(date).format("YYYY-MM-DD"),
        endTime: "10:00",
        location: "",
        physician: "",
        notes: "",
        reminders: true,
        color: "#1A5EA8",
      });
    }
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedEvent(null);
  };

  const handleDetailsDialogOpen = (event) => {
    setSelectedEvent(event);
    setDetailsDialogOpen(true);
  };

  const handleDetailsDialogClose = () => {
    setDetailsDialogOpen(false);
    setSelectedEvent(null);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveEvent = () => {
    if (!formData.title || !formData.startDate || !formData.startTime) {
      alert("Please fill in required fields: Title, Date, and Time");
      return;
    }

    const startDateTime = moment(
      `${formData.startDate} ${formData.startTime}`
    ).toDate();
    const endDateTime = moment(
      `${formData.endDate} ${formData.endTime}`
    ).toDate();

    const eventData = {
      title: formData.title,
      start: startDateTime,
      end: endDateTime,
      trialName: formData.trialName,
      visitType: formData.visitType,
      location: formData.location,
      physician: formData.physician,
      notes: formData.notes,
      reminders: formData.reminders,
      color: formData.color,
      checklist: selectedEvent?.checklist || [],
      completed: selectedEvent?.completed || false,
    };

    if (selectedEvent) {
      setEvents((prev) =>
        prev.map((e) =>
          e.id === selectedEvent.id ? { ...eventData, id: e.id } : e
        )
      );
    } else {
      setEvents((prev) => [
        ...prev,
        { ...eventData, id: Math.max(...prev.map((e) => e.id), 0) + 1 },
      ]);
    }

    handleDialogClose();
  };

  const handleDeleteEvent = (eventId) => {
    if (window.confirm("Are you sure you want to delete this appointment?")) {
      setEvents((prev) => prev.filter((e) => e.id !== eventId));
      handleDetailsDialogClose();
    }
  };

  const handleToggleChecklistItem = (eventId, checklistItemId) => {
    setEvents((prev) =>
      prev.map((event) => {
        if (event.id === eventId) {
          return {
            ...event,
            checklist: event.checklist.map((item) =>
              item.id === checklistItemId
                ? { ...item, completed: !item.completed }
                : item
            ),
          };
        }
        return event;
      })
    );

    // Update selectedEvent if it's open
    if (selectedEvent?.id === eventId) {
      setSelectedEvent((prev) => ({
        ...prev,
        checklist: prev.checklist.map((item) =>
          item.id === checklistItemId
            ? { ...item, completed: !item.completed }
            : item
        ),
      }));
    }
  };

  const handleMarkComplete = (eventId) => {
    setEvents((prev) =>
      prev.map((e) =>
        e.id === eventId ? { ...e, completed: !e.completed } : e
      )
    );

    if (selectedEvent?.id === eventId) {
      setSelectedEvent((prev) => ({ ...prev, completed: !prev.completed }));
    }
  };

  const handleAddChecklistItem = (eventId, task) => {
    if (!task.trim()) return;

    setEvents((prev) =>
      prev.map((event) => {
        if (event.id === eventId) {
          return {
            ...event,
            checklist: [
              ...(event.checklist || []),
              {
                id:
                  Math.max(...(event.checklist?.map((i) => i.id) || [0]), 0) +
                  1,
                task,
                completed: false,
              },
            ],
          };
        }
        return event;
      })
    );

    if (selectedEvent?.id === eventId) {
      setSelectedEvent((prev) => ({
        ...prev,
        checklist: [
          ...(prev.checklist || []),
          {
            id: Math.max(...(prev.checklist?.map((i) => i.id) || [0]), 0) + 1,
            task,
            completed: false,
          },
        ],
      }));
    }
  };

  const handleExportToGoogleCalendar = () => {
    alert(
      "Google Calendar export coming soon! This will create .ics files you can import."
    );
  };

  const handleSyncCalendar = () => {
    alert(
      "Calendar sync coming soon! This will enable automatic sync with Google Calendar or Outlook."
    );
  };

  const eventStyleGetter = (event) => {
    return {
      style: {
        backgroundColor: event.completed ? "#9e9e9e" : event.color,
        borderRadius: "4px",
        opacity: event.completed ? 0.6 : 0.9,
        color: "white",
        border: "0px",
        display: "block",
        textDecoration: event.completed ? "line-through" : "none",
      },
    };
  };

  const handleSelectSlot = useCallback(({ start }) => {
    setDate(start);
    handleDialogOpen();
  }, []);

  const handleSelectEvent = useCallback((event) => {
    handleDetailsDialogOpen(event);
  }, []);

  const getUpcomingVisits = () => {
    return events
      .filter((e) => moment(e.start).isAfter(moment()) && !e.completed)
      .sort((a, b) => moment(a.start).diff(moment(b.start)))
      .slice(0, 5);
  };

  const getCompletedCount = () => {
    return events.filter((e) => e.completed).length;
  };

  const getChecklistProgress = (event) => {
    if (!event.checklist || event.checklist.length === 0) return 0;
    const completed = event.checklist.filter((item) => item.completed).length;
    return Math.round((completed / event.checklist.length) * 100);
  };

  const upcomingVisits = getUpcomingVisits();

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <CalendarTodayIcon sx={{ fontSize: 40, color: "primary.main" }} />
          <Typography variant="h3" fontWeight="bold">
            Trial Calendar
          </Typography>
          <Chip label="Premium" color="warning" icon={<CheckCircleIcon />} />
        </Box>
        <Typography variant="h6" color="text.secondary">
          Manage trial visits, appointments, and stay organized
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Upcoming & Stats */}
        <Grid item xs={12} md={3}>
          {/* Add Appointment Button */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => handleDialogOpen()}
            sx={{
              background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
              py: 1.5,
              mb: 2,
            }}
          >
            Add Appointment
          </Button>

          {/* Stats Cards */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <Card elevation={2}>
                <CardContent>
                  <Typography
                    variant="h3"
                    fontWeight="bold"
                    color="primary.main"
                  >
                    {upcomingVisits.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Upcoming Visits
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card elevation={2}>
                <CardContent>
                  <Typography
                    variant="h3"
                    fontWeight="bold"
                    color="success.main"
                  >
                    {getCompletedCount()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed Visits
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card elevation={2}>
                <CardContent>
                  <Typography
                    variant="h3"
                    fontWeight="bold"
                    color="text.secondary"
                  >
                    {events.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Appointments
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Upcoming Visits List */}
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Upcoming Visits
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {upcomingVisits.length > 0 ? (
                <List dense>
                  {upcomingVisits.map((event) => (
                    <ListItem
                      key={event.id}
                      sx={{
                        px: 0,
                        cursor: "pointer",
                        "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                      }}
                      onClick={() => handleDetailsDialogOpen(event)}
                    >
                      <ListItemIcon>
                        <EventIcon sx={{ color: event.color }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography variant="body2" fontWeight="bold">
                              {moment(event.start).format("MMM DD")}
                            </Typography>
                            {event.reminders && (
                              <NotificationsActiveIcon
                                fontSize="small"
                                color="action"
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="caption" display="block">
                              {event.title}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {moment(event.start).format("h:mm A")}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                  sx={{ py: 2 }}
                >
                  No upcoming visits scheduled
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<SyncIcon />}
              onClick={handleSyncCalendar}
            >
              Sync Calendar
            </Button>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportToGoogleCalendar}
            >
              Export to .ics
            </Button>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() => window.print()}
            >
              Print Schedule
            </Button>
          </Box>
        </Grid>

        {/* Right Column - Calendar */}
        <Grid item xs={12} md={9}>
          <Paper elevation={3} sx={{ p: 2 }}>
            <Box sx={{ height: 650 }}>
              <BigCalendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                view={view}
                onView={setView}
                date={date}
                onNavigate={setDate}
                onSelectSlot={handleSelectSlot}
                onSelectEvent={handleSelectEvent}
                selectable
                eventPropGetter={eventStyleGetter}
                style={{ height: "100%" }}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Add/Edit Event Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedEvent ? "Edit Appointment" : "Add New Appointment"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Appointment Title"
                required
                value={formData.title}
                onChange={(e) => handleInputChange("title", e.target.value)}
                placeholder="e.g., Week 4 Follow-up Visit"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Trial Name"
                value={formData.trialName}
                onChange={(e) => handleInputChange("trialName", e.target.value)}
                placeholder="e.g., ATTR-ACT Extension"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Visit Type</InputLabel>
                <Select
                  value={formData.visitType}
                  label="Visit Type"
                  onChange={(e) =>
                    handleInputChange("visitType", e.target.value)
                  }
                >
                  {visitTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                required
                value={formData.startDate}
                onChange={(e) => handleInputChange("startDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Time"
                type="time"
                required
                value={formData.startTime}
                onChange={(e) => handleInputChange("startTime", e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={formData.endDate}
                onChange={(e) => handleInputChange("endDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Time"
                type="time"
                value={formData.endTime}
                onChange={(e) => handleInputChange("endTime", e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={(e) => handleInputChange("location", e.target.value)}
                placeholder="e.g., Mayo Clinic - Rochester"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Physician / Coordinator"
                value={formData.physician}
                onChange={(e) => handleInputChange("physician", e.target.value)}
                placeholder="e.g., Dr. Smith"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => handleInputChange("notes", e.target.value)}
                placeholder="Add any preparation notes, requirements, or reminders..."
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Calendar Color</InputLabel>
                <Select
                  value={formData.color}
                  label="Calendar Color"
                  onChange={(e) => handleInputChange("color", e.target.value)}
                >
                  {trialColors.map((color) => (
                    <MenuItem key={color.value} value={color.value}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            bgcolor: color.value,
                          }}
                        />
                        {color.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.reminders}
                    onChange={(e) =>
                      handleInputChange("reminders", e.target.checked)
                    }
                  />
                }
                label="Enable email/SMS reminders"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button
            onClick={handleSaveEvent}
            variant="contained"
            sx={{
              background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
            }}
          >
            {selectedEvent ? "Update" : "Add"} Appointment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Event Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleDetailsDialogClose}
        maxWidth="sm"
        fullWidth
      >
        {selectedEvent && (
          <>
            <DialogTitle>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "start",
                }}
              >
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {selectedEvent.title}
                  </Typography>
                  {selectedEvent.trialName && (
                    <Chip
                      label={selectedEvent.trialName}
                      size="small"
                      sx={{
                        mt: 1,
                        bgcolor: `${selectedEvent.color}20`,
                        color: selectedEvent.color,
                      }}
                    />
                  )}
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      handleDetailsDialogClose();
                      handleDialogOpen(selectedEvent);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteEvent(selectedEvent.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2}>
                {/* Date & Time */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <AccessTimeIcon color="action" />
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {moment(selectedEvent.start).format(
                          "dddd, MMMM DD, YYYY"
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {moment(selectedEvent.start).format("h:mm A")} -{" "}
                        {moment(selectedEvent.end).format("h:mm A")}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Visit Type */}
                {selectedEvent.visitType && (
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <ScienceIcon color="action" />
                      <Typography variant="body2">
                        <strong>Visit Type:</strong> {selectedEvent.visitType}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* Location */}
                {selectedEvent.location && (
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <LocationOnIcon color="action" />
                      <Typography variant="body2">
                        {selectedEvent.location}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* Physician */}
                {selectedEvent.physician && (
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>Provider:</strong> {selectedEvent.physician}
                    </Typography>
                  </Grid>
                )}

                {/* Notes */}
                {selectedEvent.notes && (
                  <Grid item xs={12}>
                    <Alert severity="info" icon={<WarningIcon />}>
                      <Typography variant="body2">
                        {selectedEvent.notes}
                      </Typography>
                    </Alert>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                {/* Pre-Visit Checklist */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      Pre-Visit Checklist
                    </Typography>
                    {selectedEvent.checklist &&
                      selectedEvent.checklist.length > 0 && (
                        <Chip
                          label={`${getChecklistProgress(
                            selectedEvent
                          )}% Complete`}
                          size="small"
                          color={
                            getChecklistProgress(selectedEvent) === 100
                              ? "success"
                              : "default"
                          }
                        />
                      )}
                  </Box>

                  {selectedEvent.checklist &&
                  selectedEvent.checklist.length > 0 ? (
                    <List dense>
                      {selectedEvent.checklist.map((item) => (
                        <ListItem
                          key={item.id}
                          secondaryAction={
                            <Checkbox
                              edge="end"
                              checked={item.completed}
                              onChange={() =>
                                handleToggleChecklistItem(
                                  selectedEvent.id,
                                  item.id
                                )
                              }
                            />
                          }
                          sx={{ px: 0 }}
                        >
                          <ListItemText
                            primary={item.task}
                            sx={{
                              textDecoration: item.completed
                                ? "line-through"
                                : "none",
                              opacity: item.completed ? 0.6 : 1,
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No checklist items yet
                    </Typography>
                  )}

                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Add checklist item..."
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleAddChecklistItem(
                          selectedEvent.id,
                          e.target.value
                        );
                        e.target.value = "";
                      }
                    }}
                    sx={{ mt: 1 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                {/* Reminders */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={selectedEvent.reminders}
                        onChange={(e) => {
                          setEvents((prev) =>
                            prev.map((event) =>
                              event.id === selectedEvent.id
                                ? { ...event, reminders: e.target.checked }
                                : event
                            )
                          );
                          setSelectedEvent((prev) => ({
                            ...prev,
                            reminders: e.target.checked,
                          }));
                        }}
                      />
                    }
                    label="Send reminders (24h and 1h before)"
                  />
                </Grid>

                {/* Mark Complete */}
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant={selectedEvent.completed ? "outlined" : "contained"}
                    color={selectedEvent.completed ? "default" : "success"}
                    startIcon={<AssignmentTurnedInIcon />}
                    onClick={() => handleMarkComplete(selectedEvent.id)}
                  >
                    {selectedEvent.completed
                      ? "Mark as Incomplete"
                      : "Mark as Completed"}
                  </Button>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDetailsDialogClose}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Disclaimer */}
      <Paper elevation={1} sx={{ mt: 4, p: 3, bgcolor: "grey.50" }}>
        <Typography variant="caption" color="text.secondary">
          <strong>Reminder:</strong> This calendar is for organizational
          purposes. Always confirm appointment times directly with your trial
          coordinator or healthcare provider. Set up backup reminders on your
          phone to ensure you don't miss important visits.
        </Typography>
      </Paper>
    </Container>
  );
};

export default TrialCalendarPage;
